import { useRef, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useRecording } from "../hooks/useRecording";
import TimelineEventRow, { getEventColor } from "../components/TimelineEvent";
import { NAV_HEIGHT } from "../components/NavBar";

const EVENT_TYPES = [
  "input.user",
  "agent.tool_call",
  "agent.tool_result",
  "test.step",
  "app.ready",
  "screenshot",
] as const;

export default function RecordingReplay() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const sessionId = id ?? "";
  const { events, videoUrl, loading, error } = useRecording(sessionId);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [activeTypes, setActiveTypes] = useState<Set<string>>(new Set());
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const toggleType = (type: string) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const filteredEvents =
    activeTypes.size === 0 ? events : events.filter((e) => activeTypes.has(e.type));

  const seekTo = (t_ms: number) => {
    if (videoRef.current) videoRef.current.currentTime = t_ms / 1000;
  };

  if (loading) {
    return (
      <div style={centeredStyle}>Loading recording…</div>
    );
  }

  if (error !== null) {
    return (
      <div role="alert" style={{ ...centeredStyle, color: "#fca5a5" }}>
        Error: {error}
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: `calc(100vh - ${NAV_HEIGHT}px)`,
        fontFamily: "system-ui, sans-serif",
        color: "#e5e7eb",
        background: "#0a0a0a",
      }}
    >
      <div
        style={{
          padding: "12px 20px",
          borderBottom: "1px solid #1f2937",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "none",
            border: "none",
            color: "#9ca3af",
            cursor: "pointer",
            fontSize: 18,
            lineHeight: 1,
            padding: 0,
          }}
          aria-label="Go back"
        >
          ←
        </button>
        <h1 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
          Recording Replay
        </h1>
        <span style={{ fontSize: 12, color: "#6b7280" }}>
          {sessionId.length > 8 ? `${sessionId.slice(0, 8)}…` : sessionId}
        </span>
      </div>

      <div
        style={{
          padding: "8px 20px",
          borderBottom: "1px solid #111827",
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontSize: 11,
            color: "#6b7280",
            fontWeight: 500,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
          }}
        >
          Filter:
        </span>
        {EVENT_TYPES.map((type) => {
          const active = activeTypes.has(type);
          return (
            <button
              key={type}
              onClick={() => toggleType(type)}
              style={{
                background: active ? getEventColor(type, {}) : "transparent",
                color: active ? "#fff" : "#6b7280",
                border: `1px solid ${active ? getEventColor(type, {}) : "#374151"}`,
                borderRadius: 20,
                padding: "3px 10px",
                fontSize: 11,
                cursor: "pointer",
                fontWeight: active ? 600 : 400,
              }}
            >
              {type}
            </button>
          );
        })}
        {activeTypes.size > 0 && (
          <button
            onClick={() => setActiveTypes(new Set())}
            style={{
              background: "transparent",
              color: "#9ca3af",
              border: "1px solid #374151",
              borderRadius: 20,
              padding: "3px 10px",
              fontSize: 11,
              cursor: "pointer",
            }}
          >
            Clear
          </button>
        )}
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#6b7280" }}>
          {filteredEvents.length} events
        </span>
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            flex: isMobile ? "none" : 2,
            padding: 16,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            overflowY: "auto",
          }}
        >
          <video
            ref={videoRef}
            src={videoUrl ?? undefined}
            controls
            style={{
              width: "100%",
              maxHeight: isMobile ? 220 : "calc(100vh - 220px)",
              borderRadius: 8,
              background: "#111",
            }}
          />
        </div>

        <div
          style={{
            flex: isMobile ? "none" : 1,
            overflowY: "auto",
            borderLeft: isMobile ? "none" : "1px solid #1f2937",
            borderTop: isMobile ? "1px solid #1f2937" : "none",
            maxHeight: isMobile ? 280 : "auto",
            minWidth: isMobile ? "auto" : 280,
          }}
        >
          {filteredEvents.length === 0 ? (
            <div
              style={{
                padding: 20,
                color: "#6b7280",
                textAlign: "center",
                fontSize: 13,
              }}
            >
              {events.length === 0 ? "No timeline events recorded." : "No events match the filter."}
            </div>
          ) : (
            filteredEvents.map((event, idx) => (
              <TimelineEventRow
                key={idx}
                event={event}
                onClick={() => seekTo(event.t_ms)}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

const centeredStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  color: "#9ca3af",
  fontFamily: "system-ui, sans-serif",
};
